import * as actionType from "./actionTypes";
import { getRequestAuthed, postRequestAuthed, processError } from "../../utils/apiHandlers";
import { LOADING_STATE, LS_ITEM, NOTIF_TYPE } from "../../utils/stateTypes";
import _notif from "../notification";

/* =========================== LIBRARY =========================== */

export const loadOverview = () => (
  (dispatch) => {
    const lsItem = LS_ITEM.LIBRARY;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    getRequestAuthed("/Library/Overview",
      (overview) => {
        dispatch({ type: actionType.SET_OVERVIEW, overview });
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const loadCollection = (slug) => (
  (dispatch) => {
    const lsItem = LS_ITEM.COLLECTION;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    getRequestAuthed(`/Library/Collection/${encodeURIComponent(slug)}`,
      (data) => {
        dispatch({ type: actionType.SET_COLLECTION, slug, data });
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      (error) => {
        if (error && error.locked) {
          //Not an error as such - the page shows how to unlock it
          dispatch({ type: actionType.SET_COLLECTION, slug, data: error });
          dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
          return;
        }
        processError(dispatch, _notif, lsItem)(error);
      },
    );
  }
);

//Fire and forget - counts a play for the stats
export const recordWatch = (classId) => (
  (dispatch) => {
    dispatch({ type: actionType.MARK_WATCHED, classId });
    postRequestAuthed("/Library/Watched",
      { classId },
      () => {/* Nothing to do */},
      () => {/* Not worth bothering the member about */},
    );
  }
);

/* =========================== CHECKOUT =========================== */

export const loadCheckoutPreview = (planCode, interval, currency, successCB, errorCB) => (
  (dispatch) => {
    getRequestAuthed(`/Membership/CheckoutPreview?planCode=${encodeURIComponent(planCode)}&interval=${interval}&currency=${currency}`,
      successCB,
      (error) => {
        processError(dispatch, _notif)(error);
        errorCB();
      },
    );
  }
);

// Starts the checkout: rand payments get a signed PayFast form that is posted straight away, dollar payments get the
// PayPal page to go to (either way the browser leaves the site to pay)
export const startCheckout = (planCode, interval, currency) => (
  (dispatch) => {
    const lsItem = LS_ITEM.CHECKOUT;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequestAuthed("/Membership/Checkout",
      { planCode, interval, currency },
      (result) => {
        if (result.type === "redirect") {
          window.location.assign(result.url);
          return;
        }
        const form = document.createElement("form");
        form.method = "POST";
        form.action = result.processUrl;
        for (const field of result.fields) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = field.name;
          input.value = field.value;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
        //Leave the loading state on while the browser navigates away
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

//Back from PayPal - ask the server to read the result from PayPal straight away
export const confirmPayPal = (reference, doneCB) => (
  () => {
    postRequestAuthed("/Membership/ConfirmPayPal",
      { reference },
      () => doneCB(),
      () => doneCB(),
    );
  }
);

export const checkCheckoutStatus = (reference, successCB, errorCB) => (
  (dispatch) => {
    getRequestAuthed(`/Membership/CheckoutStatus?reference=${encodeURIComponent(reference)}`,
      successCB,
      (error) => {
        processError(dispatch, _notif, null, null, true)(error);
        errorCB(error);
      },
    );
  }
);

//Local development only - see MembershipController.DevCompleteCheckout
export const devCompleteCheckout = (reference, successCB) => (
  (dispatch) => {
    postRequestAuthed("/Membership/DevCompleteCheckout",
      { reference },
      successCB,
      processError(dispatch, _notif),
    );
  }
);

/* =========================== MY ACCOUNT =========================== */

export const loadMembership = () => (
  (dispatch) => {
    const lsItem = LS_ITEM.MEMBERSHIP;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    getRequestAuthed("/Membership/Mine",
      (membership) => {
        dispatch({ type: actionType.SET_MEMBERSHIP, membership });
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const changePlan = (planCode, interval, successCB) => (
  (dispatch) => {
    const lsItem = LS_ITEM.CHANGE_PLAN;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequestAuthed("/Membership/ChangePlan",
      { planCode, interval },
      (result) => {
        if (result && result.redirectUrl) {
          //PayPal needs the member to approve the new price - they come back to the account page
          window.location.assign(result.redirectUrl);
          return;
        }
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        dispatch(_notif.actions.queueNotification("Your plan has been updated", NOTIF_TYPE.SUCCESS));
        dispatch(loadMembership());
        successCB();
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

//Back from approving a PayPal plan change
export const confirmRevision = (reference) => (
  (dispatch) => {
    postRequestAuthed("/Membership/ConfirmRevision",
      { reference },
      (result) => {
        dispatch(_notif.actions.queueNotification((result && result.message) || "Your plan has been updated", (result && result.message) ? NOTIF_TYPE.STANDARD : NOTIF_TYPE.SUCCESS));
        dispatch(loadMembership());
      },
      processError(dispatch, _notif),
    );
  }
);

export const cancelSubscription = (reason, successCB) => (
  (dispatch) => {
    const lsItem = LS_ITEM.CANCEL;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequestAuthed("/Membership/Cancel",
      { reason },
      () => {
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        dispatch(_notif.actions.queueNotification("Your subscription has been cancelled", NOTIF_TYPE.SUCCESS));
        dispatch(loadMembership());
        successCB();
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

/* =========================== COMMENTS =========================== */

export const loadComments = (classId, successCB) => (
  (dispatch) => {
    getRequestAuthed(`/Library/Comments/${classId}`,
      successCB,
      processError(dispatch, _notif, null, null, true),
    );
  }
);

export const addComment = (classId, text, parentId, successCB, errorCB) => (
  (dispatch) => {
    postRequestAuthed("/Library/AddComment",
      { classId, text, parentId },
      successCB,
      processError(dispatch, _notif, null, errorCB),
    );
  }
);

export const deleteComment = (id, successCB) => (
  (dispatch) => {
    postRequestAuthed("/Library/DeleteComment",
      { id },
      successCB,
      processError(dispatch, _notif),
    );
  }
);

/* =========================== WEEKLY PLAN =========================== */

export const loadWeeklyPlan = (successCB, errorCB) => (
  (dispatch) => {
    getRequestAuthed("/Library/WeeklyPlan",
      successCB,
      (error) => {
        if (error && error.locked) {
          errorCB(error);
          return;
        }
        processError(dispatch, _notif)(error);
        errorCB(error);
      },
    );
  }
);
